import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatLegacySelect as MatSelect } from '@angular/material/legacy-select';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { connectState, InputObservable, trackByValue } from '@y42/angular/util';
import { combineLatest, map, Observable, startWith } from 'rxjs';

@UntilDestroy()
@Component({
  selector: 'y42-search-dropdown[itemTemplate]',
  templateUrl: './search-dropdown.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchDropdownComponent<T> implements AfterViewInit {
  @InputObservable()
  @Input()
  items: T[] = [];
  private readonly items$!: Observable<T[]>;

  @Input() id?: keyof T;
  @Input() initiallyOpen = false;
  @Input() label = '';
  @Input() placeholder = '';
  @Input() searchCallback: ((item: T, searchQuery: string) => boolean) | undefined;
  @Input() itemTemplate!: TemplateRef<{ item: T; control: FormControl<T[keyof T] | T | null> }>;
  @Input() control = new FormControl<T[keyof T] | T | null>(null);

  @Output() closed = new EventEmitter<void>();

  @ViewChild(MatSelect) select!: MatSelect;

  readonly trackByValue = trackByValue;
  readonly formFilterCtrl = new FormControl<string>('', { nonNullable: true });

  readonly state = connectState(this, {
    filteredItems: combineLatest([this.items$, this.formFilterCtrl.valueChanges.pipe(startWith(''))]).pipe(
      map(([items, searchQuery]) => this.search(items, searchQuery)),
      untilDestroyed(this),
    ),
  });

  ngAfterViewInit(): void {
    if (this.initiallyOpen) {
      this.select.open();
    }
  }

  private search(items: T[], searchQuery: string): T[] {
    if (!this.searchCallback) {
      return items;
    }

    const searchCallback = this.searchCallback;
    searchQuery = searchQuery.toLowerCase();
    return searchQuery ? items.filter((item) => searchCallback(item, searchQuery)) : items;
  }
}
